import { isNumber } from 'lodash';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-lodash-debounce';

import { usePhoneOptionsContext } from '../../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import { ClientLookupCollection } from '../../../../../../types/api/clients';
import { HubCollection } from '../../../../../../types/api/hubs';
import { PlacesStreetCollection } from '../../../../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../../utils/constants/phoneNumbers';
import { queryString } from '../../../../../../utils/helpers/http';
import { tryString } from '../../../../../../utils/helpers/parse';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../utils/helpers/phoneNumbers';
import AutoCompleteInput from '../../../../../Forms/AutoCompleteInput/AutoCompleteInput';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import { PlaceCollection } from '../CreateEditDialog.functions';

type Props = {
  hubs: HubCollection | undefined;
  isConstant: boolean;
  orderIndex?: number;
  nearestRecipientHub: { ime: string; id: number } | undefined;
  isNearestRecipientHubLoading: boolean;
};

function RecipientTab({
  hubs,
  orderIndex,
  isConstant,
  nearestRecipientHub,
  isNearestRecipientHubLoading,
}: Props) {
  const { t } = useTranslation();
  const { control, setValue, watch } = useFormContext();
  const values = watch();

  const debouncedRecipient = useDebounce(values?.klient_do_ime, 300);

  const hubsSuggestions =
    hubs?.data.map((hub) => {
      return { id: hub.id, label: hub.name };
    }) ?? [];

  const { data: recipientsData, isLoading: isRecipientsDataLoading } =
    useAxiosHook<ClientLookupCollection>(
      '/clients/lookup' +
        queryString({
          ime: !isNumber(debouncedRecipient ?? '') ? debouncedRecipient : null,
          telefon: isNumber(debouncedRecipient ?? '')
            ? debouncedRecipient
            : null,
          page: 1,
          limit: 10,
        }),
      { skipWhen: !!values?.no_recipient }
    );

  const clientsSuggestions =
    recipientsData?.data.map((client) => {
      return {
        label: client.ime,
        value: String(client.id),
      };
    }) ?? [];

  const { data: places, isLoading: isPlacesLoading } =
    useAxiosHook<PlaceCollection>(`/places?limit=0`, { skipCache: true });

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${values.delivery_address.placeId}/streets?limit=0`,
    {
      skipWhen: !values.delivery_address.placeId,
      skipCache: true,
    }
  );

  const placesOptions = useMemo(
    () =>
      places && places?.data.length > 0
        ? places?.data.map((m: any) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [places]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, values.delivery_address.streetId]
  );

  const isRecipientPlaceModified =
    control?._defaultValues.delivery_address?.placeId !==
    values.delivery_address.placeId;

  const debounced_is_place_modified = useDebounce(
    isRecipientPlaceModified,
    500
  );

  useEffect(() => {
    if (debounced_is_place_modified && values?.delivery_location_type_id) {
      setValue('hub_do_id', nearestRecipientHub?.id);
    }
  }, [debounced_is_place_modified, nearestRecipientHub?.id, setValue, values]);

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const handleClientSelect = useCallback(
    (id: string | null) => {
      setValue('klient_do_id', id);

      const recipient = recipientsData?.data.find(
        (recipient) => String(recipient.id) === id
      );

      setValue('telefon_do', recipient?.telefon ?? '');
      setValue(
        'telefon_do_region',
        getPhoneorMobileNumberRegion(recipient?.telefon ?? '')
      );
      setValue('mobilen_do', recipient?.mobilen ?? '');
      setValue(
        'mobilen_do_region',
        getPhoneorMobileNumberRegion(recipient?.mobilen ?? '')
      );
      setValue('delivery_address', recipient?.address ?? '');
    },
    [recipientsData?.data, setValue]
  );

  const handleClientChange = useCallback(
    (ime: string) => {
      setValue('klient_do_ime', ime);
    },
    [setValue]
  );

  return (
    <div>
      {orderIndex !== 0 && isConstant && <div className="constant" />}

      {values?.no_recipient ? (
        <FieldWithErrors name="klient_do_ime" label={t('Recipient Name')}>
          <Controller
            name="klient_do_ime"
            render={({ field }) => (
              <InputText
                id="klient_do_ime"
                name="klient_do_ime"
                value={field.value}
                onChange={field.onChange}
                placeholder={t('Required')}
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <FieldWithErrors name="klient_do_ime" label={t('Search Clients')}>
          <AutoCompleteInput
            placeholder={
              isRecipientsDataLoading
                ? t('Loading...')
                : t('Search by name or phone number')
            }
            options={clientsSuggestions}
            value={tryString(values.klient_do_id) ?? null}
            filterValue={values?.klient_do_ime ?? ''}
            onFilterChange={handleClientChange}
            onSelectionChange={handleClientSelect}
            filterDataCy="recipient"
            optionsClassName="data-cy-recipient-options"
          />
        </FieldWithErrors>
      )}

      <FieldWithErrors
        name="no_recipient"
        label={false}
        className="p-d-flex p-mb-2 p-jc-between"
      >
        <Controller
          name="no_recipient"
          render={({ field }) => (
            <>
              <Checkbox
                id="no_recipient"
                name="no_recipient"
                value={field.value}
                checked={field.value}
                onChange={(e) => {
                  if (e.checked) {
                    setValue('klient_do_id', '2');
                    setValue('no_recipient', true);
                  } else {
                    setValue('klient_do_id', '');
                    setValue('no_recipient', false);
                  }
                }}
              />
              <label htmlFor="no_sender" data-cy="no_sender">
                {t('Unknown client')}
              </label>
            </>
          )}
        />
      </FieldWithErrors>

      <FieldWithErrors name="delivery_address.placeId" label={t('Place')}>
        <Controller
          name="delivery_address.placeId"
          render={({ field }) => (
            <Dropdown
              id="delivery_address.placeId"
              name="delivery_address.placeId"
              options={placesOptions}
              value={field.value}
              onChange={(e) => {
                if (e.value) {
                  field.onChange(e.value);
                  const item = placesOptions?.find((s) => s.value === e.value);
                  setValue('delivery_address.placeName', item?.label);

                  reloadStreets({
                    url: `places/${e.value}/streets?limit=0`,
                  });
                } else {
                  setValue('delivery_address.placeId', null);
                  setValue('delivery_address.placeName', '');
                  setValue('delivery_address.streetId', null);
                  setValue('delivery_address.streetName', '');
                  setValue('delivery_address.housenumber', '');
                  setValue('delivery_address.entrance', '');
                  setValue('delivery_address.apartment', '');
                }
              }}
              filter
              filterPlaceholder={t('Search')}
              placeholder={
                isPlacesLoading ? t('Loading...') : t('Search places')
              }
              disabled={isPlacesLoading}
              className="data-cy-placeId"
            />
          )}
        />
      </FieldWithErrors>

      {isStreetFreeInputAllowed ? (
        <FieldWithErrors name="delivery_address.rawName" label={t('Address')}>
          <Controller
            name="delivery_address.rawName"
            render={({ field }) => (
              <InputText
                id="delivery_address.rawName"
                name="delivery_address.rawName"
                value={field.value}
                onChange={field.onChange}
                disabled={!values?.delivery_address.placeId || isStreetsLoading}
              />
            )}
          />
        </FieldWithErrors>
      ) : (
        <>
          <FieldWithErrors name="delivery_address.streetId" label={t('Street')}>
            <Controller
              name="delivery_address.streetId"
              render={({ field }) => (
                <Dropdown
                  id="delivery_address.streetId"
                  name="delivery_address.streetId"
                  options={streetsOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const item = streetsOptions?.find(
                        (s) => s.value === e.value
                      );
                      setValue('delivery_address.streetName', item?.label);
                    } else {
                      setValue('delivery_address.streetId', null);
                      setValue('delivery_address.streetName', '');
                      setValue('delivery_address.housenumber', '');
                      setValue('delivery_address.entrance', '');
                      setValue('delivery_address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={
                    isStreetsLoading ? t('Loading...') : t('Select Street')
                  }
                  disabled={
                    !values.delivery_address.placeId || isStreetsLoading
                  }
                  className="data-cy-streetId"
                />
              )}
            />
          </FieldWithErrors>

          <div className="p-grid">
            <FieldWithErrors
              name="delivery_address.housenumber"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="delivery_address.housenumber"
                render={({ field }) => (
                  <InputText
                    id="delivery_address.housenumber"
                    name="delivery_address.housenumber"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values.delivery_address.streetId}
                    placeholder={t('Street No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="delivery_address.entrance"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="delivery_address.entrance"
                render={({ field }) => (
                  <InputText
                    id="delivery_address.entrance"
                    name="delivery_address.entrance"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values.delivery_address.streetId}
                    placeholder={t('Entrance No.')}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors
              name="delivery_address.apartment"
              label={false}
              className="p-col-4"
            >
              <Controller
                name="delivery_address.apartment"
                render={({ field }) => (
                  <InputText
                    id="delivery_address.apartment"
                    name="delivery_address.apartment"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!values.delivery_address.streetId}
                    placeholder={t('Flat No.')}
                  />
                )}
              />
            </FieldWithErrors>
          </div>
        </>
      )}

      <FieldWithErrors name="mobilen_do" label={t('Mobile')}>
        <div className="p-inputgroup number-group">
          <Controller
            name="mobilen_do_region"
            render={({ field }) => (
              <Dropdown
                style={{ padding: 0, width: '5rem' }}
                id="mobilen_do_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="mobilen_do_region"
                {...field}
              />
            )}
          />
          <Controller
            name="mobilen_do"
            render={({ field }) => (
              <InputText
                name="mobilen_do"
                style={{ width: '100%' }}
                value={formatMobileNumber(
                  values?.mobilen_do,
                  values?.mobilen_do_region ||
                    getPhoneorMobileNumberRegion(values?.mobilen_do)
                )}
                onChange={(e) => {
                  field.onChange(
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
                className="data-cy-mobile"
              />
            )}
          />
        </div>
      </FieldWithErrors>

      <FieldWithErrors name="telefon_do" label={t('Phone')}>
        <div className="p-inputgroup">
          <Controller
            name="telefon_do_region"
            render={({ field }) => (
              <Dropdown
                style={{ padding: 0, width: '5rem' }}
                id="telefon_do_region"
                options={languages}
                itemTemplate={countryOptionTemplate}
                valueTemplate={selectedCountryTemplate}
                data-cy="telefon_do_region"
                {...field}
              />
            )}
          />
          <Controller
            name="telefon_do"
            render={({ field }) => (
              <InputText
                name="telefon_do"
                style={{ width: '100%' }}
                value={formatPhoneNumber(
                  values?.telefon_do,
                  values?.telefon_do_region ||
                    getPhoneorMobileNumberRegion(values?.telefon_do)
                )}
                onChange={(e) => {
                  field.onChange(
                    e.target.value?.replace(
                      invalidPhoneNumberCharactersRegex,
                      ''
                    )
                  );
                }}
                className="data-cy-telefon"
              />
            )}
          />
        </div>
      </FieldWithErrors>

      <FieldWithErrors
        name="delivery_location_type_id"
        label={t('Recipient picks up from warehouse')}
      >
        <div className="p-d-flex p-ai-center">
          <Controller
            name="delivery_location_type_id"
            render={({ field }) => (
              <Checkbox
                id="delivery_location_type_id"
                name="delivery_location_type_id"
                value={field.value}
                checked={field.value}
                onChange={(e) => {
                  setValue('delivery_location_type_id', e.checked);

                  if (!e.checked) {
                    setValue('hub_do_id', '');
                    return;
                  }
                  setValue('hub_do_id', nearestRecipientHub?.id);
                }}
                className="hub-checkbox data-cy-deliver-to-hub-cbx"
              />
            )}
          />
          <Controller
            name="hub_do_id"
            render={({ field }) => (
              <Dropdown
                id="hub_do_id"
                name="hub_do_id"
                options={hubsSuggestions}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                filter
                filterPlaceholder={t('Search')}
                disabled={!values?.delivery_location_type_id}
                className="data-cy-hub_do_id"
                placeholder={
                  isNearestRecipientHubLoading &&
                  values?.delivery_location_type_id
                    ? t('Loading...')
                    : undefined
                }
              />
            )}
          />
        </div>
      </FieldWithErrors>
    </div>
  );
}

export default RecipientTab;
